// @tennis-matches/services/supabase
// Database repository
// Migrated from src/services/supabase/db.repository.js

import { getSupabaseClient, getPhase1TenantId } from "./supabase.service.js";
const I18N_QUERY_TIMEOUT_MS = 2500;

// ------------------------------------------------------------
// Private Helpers
// ------------------------------------------------------------
function __isUuid(v) {
  const x = String(v || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    x,
  );
}

function __isI18nVerboseLogEnabled() {
  try {
    return (
      typeof import.meta !== "undefined" &&
      !!import.meta.env?.DEV &&
      typeof localStorage !== "undefined" &&
      localStorage.getItem("tm_debug_i18n") === "1"
    );
  } catch {
    return false;
  }
}

/** Debug-Beispielkeys: Shell/Login — nicht data.slice(0,3) (alphabetisch → minigames.*). */
function __i18nDevSampleKeys(allKeys) {
  const keys = Array.isArray(allKeys) ? allKeys : [];
  const prefer = [
    "title.app",
    "auth.login.title",
    "auth.register.title",
    "nav.settings",
    "accessPending.title",
  ];
  const set = new Set(keys);
  const picked = prefer.filter((k) => set.has(k));
  return picked.length ? picked : keys.slice(0, 3);
}

function __getCachedTenantId() {
  try {
    // First try phase1 tenant ID (from config)
    const phase1TenantId = getPhase1TenantId();
    if (__isUuid(phase1TenantId)) return phase1TenantId;

    // Fallback to localStorage
    const ls =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("ttb_active_tenant_id")
        : null;
    return __isUuid(ls) ? String(ls).trim() : null;
  } catch {
    return null;
  }
}

function __parseMatchKey(matchKey) {
  const m = String(matchKey || "")
    .trim()
    .match(/^r(\d+)m(\d+)$/i);
  if (!m) return null;
  const roundNo = Number(m[1] || 0);
  const matchNo = Number(m[2] || 0);
  if (roundNo <= 0 || matchNo <= 0) return null;
  return { roundNo, matchNo };
}

function __isAbortError(err) {
  const name = String(err?.name || "").toLowerCase();
  const msg = String(err?.message || "").toLowerCase();
  return name === "aborterror" || msg.includes("aborted");
}

async function __runWithAbortTimeout(
  queryBuilder,
  timeoutMs = I18N_QUERY_TIMEOUT_MS,
) {
  const ctrl =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer =
    ctrl && typeof setTimeout === "function"
      ? setTimeout(
          () => {
            try {
              ctrl.abort();
            } catch (_) {}
          },
          Number(timeoutMs || I18N_QUERY_TIMEOUT_MS),
        )
      : null;
  try {
    let q = queryBuilder;
    if (ctrl && typeof q?.abortSignal === "function")
      q = q.abortSignal(ctrl.signal);
    return await q;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function __mkMatchKey(roundNo, matchNo) {
  const r = Number(roundNo || 0);
  const m = Number(matchNo || 0);
  if (r <= 0 || m <= 0) return "";
  return `r${r}m${m}`;
}

const __matchIdCache = new Map(); // key: `${tournamentCode}|rNmM` -> match_uuid

async function __getAuthedUserId(supabase) {
  try {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Clean up existing tournament sub-data to prevent orphans before re-inserting
 * CRITICAL: Does NOT delete match_results - results are independent of schedule changes
 */
async function __clearTournamentSubData(supabase, tournamentId) {
  if (!tournamentId) return;
  // Order matters for FK constraints
  // NOTE: match_results are NOT deleted - they persist across schedule updates
  // Only delete matches/rounds/players, not results
  await supabase
    .from("tournament_matches")
    .delete()
    .eq("tournament_id", tournamentId);
  await supabase
    .from("tournament_rounds")
    .delete()
    .eq("tournament_id", tournamentId);
  await supabase
    .from("tournament_players")
    .delete()
    .eq("tournament_id", tournamentId);

  // Clear cache for this tournament
  for (const k of Array.from(__matchIdCache.keys())) {
    if (k.startsWith(tournamentId + "|")) __matchIdCache.delete(k);
  }
}

// ------------------------------------------------------------
// Repository
// ------------------------------------------------------------
export const dbRepository = {
  /**
   * Loads i18n messages via REST-exposed objects in public schema.
   * Source of truth: public.v_i18n_messages (columns: locale, key, text, resolved_via)
   * Fallback: public.i18n_messages (columns: tenant_id, locale, key, text)
   *
   * Returns: { [key]: text }
   */
  async getI18nMessages(locale) {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      console.warn("[i18n] No Supabase client available");
      return {};
    }

    try {
      const locRaw = String(locale || "").trim();
      if (!locRaw) {
        console.warn("[i18n] No locale provided");
        return {};
      }

      // Normalize locale: "de_de" -> "de-DE", "de-DE" -> "de-DE", "de" -> "de"
      const locNorm = (() => {
        const s = String(locRaw).trim().replaceAll("_", "-");
        const parts = s.split("-").filter(Boolean);
        if (!parts.length) return "de";
        const lang = String(parts[0] || "de").toLowerCase();
        const region = parts[1] ? String(parts[1]).toUpperCase() : "";
        return region ? `${lang}-${region}` : lang;
      })();

      // Candidate locales to try (robust against DB storing "de" vs "de-DE")
      const locCandidates = (() => {
        const lang = String(locNorm.split("-")[0] || "de").toLowerCase();
        const out = [];
        const push = (x) => {
          const v = String(x || "").trim();
          if (v && !out.includes(v)) out.push(v);
        };

        // prefer most specific first
        // common special-case: app uses "de", DB often uses "de-DE"
        if (locNorm === "de") {
          push("de-DE");
          push("de");
          return out;
        }

        // exact locale first
        push(locNorm);
        // language-only fallback for regioned locales (e.g. de-DE -> de)
        if (locNorm.includes("-")) push(lang);

        return out;
      })();

      console.log(
        `[i18n] Loading messages for locale: "${locNorm}" (candidates: ${locCandidates.join(", ")})`,
      );

      const tenantId = this.getContext?.().tenantId || null;

      // PRIORITY 1: Try reading from i18n_keys via view (new source of truth)
      let data = null;
      let error = null;

      // Try view first (reads from i18n.i18n_keys via public.v_i18n_keys_messages)
      console.log("[i18n] Attempting to load from v_i18n_keys_messages...");
      for (const candidate of locCandidates) {
        ({ data, error } = await __runWithAbortTimeout(
          supabase
            .from("v_i18n_keys_messages")
            .select("key,text")
            .eq("locale", candidate),
        ));
        if (__isAbortError(error)) {
          return {};
        }
        if (!error && Array.isArray(data) && data.length > 0) {
          console.log(
            `[i18n] ✅ Loaded ${data.length} messages from i18n_keys via view (locale: "${candidate}")`,
          );
          break;
        }
      }

      if (error) {
        console.warn("[i18n] Error loading from v_i18n_keys_messages:", error);
      } else {
        console.log(
          `[i18n] v_i18n_keys_messages returned ${Array.isArray(data) ? data.length : 0} rows`,
        );
        if (
          Array.isArray(data) &&
          data.length > 0 &&
          __isI18nVerboseLogEnabled()
        ) {
          console.log(
            "[i18n] Sample keys from view:",
            __i18nDevSampleKeys(data.map((r) => r.key)),
          );
        }
      }

      // If view fails, try RPC function
      if (error || !Array.isArray(data) || data.length == 0) {
        console.log("[i18n] Trying RPC get_i18n_keys...");
        try {
          for (const candidate of locCandidates) {
            const { data: rpcData, error: rpcError } =
              await __runWithAbortTimeout(
                supabase.rpc("get_i18n_keys", { p_locale: candidate }),
              );

            if (rpcError) {
              if (__isAbortError(rpcError)) {
                return {};
              }
              console.warn(
                `[i18n] RPC error (locale: "${candidate}"):`,
                rpcError,
              );
              continue;
            }
            if (Array.isArray(rpcData) && rpcData.length > 0) {
              data = rpcData.map((row) => ({
                key: row.key,
                text: row.text || "",
              }));
              error = null;
              console.log(
                `[i18n] ✅ Loaded ${rpcData.length} messages from i18n_keys via RPC (locale: "${candidate}")`,
              );
              if (__isI18nVerboseLogEnabled()) {
                console.log(
                  "[i18n] Sample keys from RPC:",
                  __i18nDevSampleKeys(data.map((r) => r.key)),
                );
              }
              break;
            }
          }
        } catch (rpcErr) {
          console.warn("[i18n] RPC get_i18n_keys exception:", rpcErr);
        }
      }

      // PRIORITY 2: Fallback to old views/tables (for backwards compatibility)
      if (error || !Array.isArray(data) || data.length == 0) {
        console.log("[i18n] Falling back to v_i18n_messages...");
        for (const candidate of locCandidates) {
          let q = supabase
            .from("v_i18n_messages")
            .select("key,text")
            .eq("locale", candidate);

          ({ data, error } = await __runWithAbortTimeout(q));
          if (__isAbortError(error)) {
            return {};
          }
          if (!error && Array.isArray(data) && data.length > 0) break;
        }

        if (error) {
          console.warn("[i18n] Error loading from v_i18n_messages:", error);
        } else {
          console.log(
            `[i18n] v_i18n_messages returned ${Array.isArray(data) ? data.length : 0} rows`,
          );
        }

        // Fallback to table if view fails or returns nothing
        if (error || !Array.isArray(data) || data.length == 0) {
          console.log("[i18n] Falling back to i18n_messages table...");
          for (const candidate of locCandidates) {
            let qt = supabase
              .from("i18n_messages")
              .select("key,text")
              .eq("locale", candidate);

            if (tenantId) qt = qt.eq("tenant_id", tenantId);

            ({ data, error } = await __runWithAbortTimeout(qt));
            if (__isAbortError(error)) {
              return {};
            }
            if (!error && Array.isArray(data) && data.length > 0) break;
          }

          if (error) {
            console.warn(
              "[i18n] Error loading from i18n_messages table:",
              error,
            );
          } else {
            console.log(
              `[i18n] i18n_messages table returned ${Array.isArray(data) ? data.length : 0} rows`,
            );
          }
        }
      }

      if (error || !Array.isArray(data)) {
        if (__isAbortError(error)) {
          return {};
        }
        console.warn("[i18n] No data loaded, returning empty object");
        return {};
      }

      const out = {};
      for (const row of data) {
        const k = row?.key;
        if (typeof k === "string" && k.length) {
          out[k] = String(row?.text ?? "");
        }
      }

      console.log(`[i18n] ✅ Returning ${Object.keys(out).length} messages`);
      if (__isI18nVerboseLogEnabled()) {
        console.log(
          "[i18n] Sample keys in result:",
          __i18nDevSampleKeys(Object.keys(out)),
        );
      }

      return out;
    } catch (e) {
      console.error("[i18n] getI18nMessages exception:", e);
      return {};
    }
  },

  getContext() {
    return { tenantId: __getCachedTenantId() };
  },

  async ensureTournamentByCode({
    code,
    kind = "schleifchen",
    name = "MatchMixer",
    startDate = "",
    startTime = "",
    format = "doubles",
    rounds = 6,
    minutesPerRound = 20,
    points = { win: 2, draw: 1 },
    settings: extraSettings,
  } = {}) {
    const supabase = await getSupabaseClient();
    if (!supabase || !code) return null;

    const c = String(code).trim();

    // CRITICAL: Check if tournament already exists to preserve current_round and completed_round
    // maybeSingle() damit 0 Zeilen HTTP 200 + null liefern statt 406 (Not Acceptable)
    const { data: existing } = await supabase
      .from("tournaments")
      .select("current_round, completed_round, phase")
      .eq("code", c)
      .maybeSingle();

    const baseSettings = {
      code: c,
      startDate,
      startTime: startTime || "",
      format,
      rounds,
      minutesPerRound,
      points,
    };
    const settings =
      extraSettings && typeof extraSettings === "object"
        ? { ...baseSettings, ...extraSettings }
        : baseSettings;

    const payload = {
      code: c,
      kind,
      name,
      settings,
      status: "draft",
      updated_at: new Date().toISOString(),
    };

    // CRITICAL: Only set current_round/completed_round if tournament doesn't exist
    // If it exists, preserve the existing values
    if (existing) {
      // Tournament exists - preserve current_round and completed_round
      payload.current_round = existing.current_round ?? 1;
      payload.completed_round = existing.completed_round ?? 0;
      payload.phase = existing.phase ?? "idle";
    } else {
      // New tournament - set defaults
      payload.current_round = 1;
      payload.completed_round = 0;
      payload.phase = "idle";
    }

    const uid = await __getAuthedUserId(supabase);
    if (uid) payload.created_by = uid;

    // Set tenant_id for RLS policies
    const tenantId = __getCachedTenantId();
    if (tenantId) payload.tenant_id = tenantId;

    // Upsert by code so "Setup aktualisieren" also updates meta/settings for existing tournaments.
    // This also avoids duplicate-key errors when the tournament already exists.
    const { data: created, error: cErr } = await supabase
      .from("tournaments")
      .upsert(payload, { onConflict: "code" })
      .select("id")
      .single();

    if (cErr) {
      console.error("[dbRepository] ensureTournamentByCode failed:", cErr);
      return null;
    }
    return created?.id || null;
  },

  async getMatchResults(tournamentId) {
    const supabase = await getSupabaseClient();
    if (!supabase || !tournamentId) return {};

    const [resData, roundsData, matchesData] = await Promise.all([
      supabase
        .from("match_results")
        .select("match_id, result")
        .eq("tournament_id", tournamentId),
      supabase
        .from("tournament_rounds")
        .select("id, round_no")
        .eq("tournament_id", tournamentId),
      supabase
        .from("tournament_matches")
        .select("id, round_id, match_no")
        .eq("tournament_id", tournamentId),
    ]);

    if (resData.error || roundsData.error || matchesData.error) {
      console.warn("[getMatchResults] DB error:", {
        resData: resData.error,
        roundsData: roundsData.error,
        matchesData: matchesData.error,
      });
      return {};
    }

    const roundIdToNo = new Map(roundsData.data.map((r) => [r.id, r.round_no]));
    const matchIdToKey = new Map();

    matchesData.data.forEach((m) => {
      const rNo = roundIdToNo.get(m.round_id);
      const key = __mkMatchKey(rNo, m.match_no);
      if (key) matchIdToKey.set(m.id, key);
    });

    const results = resData.data.reduce((acc, row) => {
      const key = matchIdToKey.get(row.match_id);
      if (key) {
        const result = row.result || {};
        // Ensure result has sets array if it's an object
        if (
          result &&
          typeof result === "object" &&
          !Array.isArray(result.sets)
        ) {
          // If result exists but has no sets, it might be malformed
          if (Object.keys(result).length > 0 && !result.sets) {
            console.warn(
              `[getMatchResults] Result for ${key} has no sets array:`,
              result,
            );
          }
        }
        acc[key] = result;
        console.log(
          `[getMatchResults] ✅ Mapped ${row.match_id} -> ${key}:`,
          result,
        );
      } else {
        console.warn(
          `[getMatchResults] ⚠️ No match key found for match_id: ${row.match_id}`,
        );
      }
      return acc;
    }, {});

    console.log(
      `[getMatchResults] 📊 Summary for tournament ${tournamentId}:`,
      {
        totalResultsInDb: resData.data.length,
        totalRounds: roundsData.data.length,
        totalMatches: matchesData.data.length,
        mappedResults: Object.keys(results).length,
        resultKeys: Object.keys(results),
        matchIdToKeySize: matchIdToKey.size,
        sampleMatchMapping: Array.from(matchIdToKey.entries()).slice(0, 3),
      },
    );
    return results;
  },

  async saveMatchResult({ tournamentId, matchKey, result, userId } = {}) {
    console.log("[dbRepository.saveMatchResult] START", {
      tournamentId,
      matchKey,
      hasResult: !!result,
      resultKeys: result ? Object.keys(result) : [],
    });

    const supabase = await getSupabaseClient();
    if (!supabase || !tournamentId || !matchKey) {
      console.warn("[dbRepository.saveMatchResult] Missing params", {
        hasSupabase: !!supabase,
        tournamentId,
        matchKey,
      });
      return null;
    }

    const parsed = __parseMatchKey(matchKey);
    if (!parsed) {
      console.warn("[dbRepository.saveMatchResult] Failed to parse matchKey", {
        matchKey,
      });
      return null;
    }

    console.log("[dbRepository.saveMatchResult] Parsed matchKey", {
      matchKey,
      roundNo: parsed.roundNo,
      matchNo: parsed.matchNo,
    });

    // 1. Resolve Round
    let { data: round, error: roundError } = await supabase
      .from("tournament_rounds")
      .select("id")
      .eq("tournament_id", tournamentId)
      .eq("round_no", parsed.roundNo)
      .maybeSingle();

    if (roundError) {
      console.warn("[dbRepository.saveMatchResult] Error loading round", {
        tournamentId,
        roundNo: parsed.roundNo,
        error: roundError,
      });
    }

    if (!round) {
      console.log("[dbRepository.saveMatchResult] Round not found, creating", {
        tournamentId,
        roundNo: parsed.roundNo,
      });
      const tenantId = __getCachedTenantId();
      const roundPayload = {
        tournament_id: tournamentId,
        round_no: parsed.roundNo,
      };
      if (tenantId) roundPayload.tenant_id = tenantId;

      const { data: newRound, error: insertRoundError } = await supabase
        .from("tournament_rounds")
        .insert(roundPayload)
        .select("id")
        .single();

      if (insertRoundError) {
        console.error("[dbRepository.saveMatchResult] Failed to create round", {
          tournamentId,
          roundNo: parsed.roundNo,
          error: insertRoundError,
        });
        return null;
      }

      round = newRound;
      console.log("[dbRepository.saveMatchResult] Round created", {
        tournamentId,
        roundNo: parsed.roundNo,
        roundId: round?.id,
      });
    }

    if (!round?.id) {
      console.error(
        "[dbRepository.saveMatchResult] No round ID after resolve/create",
        {
          tournamentId,
          roundNo: parsed.roundNo,
        },
      );
      return null;
    }

    // 2. Resolve Match
    let { data: match, error: matchError } = await supabase
      .from("tournament_matches")
      .select("id")
      .eq("round_id", round.id)
      .eq("match_no", parsed.matchNo)
      .maybeSingle();

    if (matchError) {
      console.warn("[dbRepository.saveMatchResult] Error loading match", {
        tournamentId,
        roundId: round.id,
        matchNo: parsed.matchNo,
        error: matchError,
      });
    }

    if (!match) {
      console.log("[dbRepository.saveMatchResult] Match not found, creating", {
        tournamentId,
        roundId: round.id,
        matchNo: parsed.matchNo,
      });
      const tenantId = __getCachedTenantId();
      const matchPayload = {
        tournament_id: tournamentId,
        round_id: round.id,
        match_no: parsed.matchNo,
        side_1: [],
        side_2: [],
      };
      if (tenantId) matchPayload.tenant_id = tenantId;

      const { data: newMatch, error: insertMatchError } = await supabase
        .from("tournament_matches")
        .insert(matchPayload)
        .select("id")
        .single();

      if (insertMatchError) {
        console.error("[dbRepository.saveMatchResult] Failed to create match", {
          tournamentId,
          roundId: round.id,
          matchNo: parsed.matchNo,
          error: insertMatchError,
        });
        return null;
      }

      match = newMatch;
      console.log("[dbRepository.saveMatchResult] Match created", {
        tournamentId,
        roundId: round.id,
        matchNo: parsed.matchNo,
        matchId: match?.id,
      });
    }

    if (!match?.id) {
      console.error(
        "[dbRepository.saveMatchResult] No match ID after resolve/create",
        {
          tournamentId,
          roundId: round.id,
          matchNo: parsed.matchNo,
        },
      );
      return null;
    }

    // 3. Upsert Result
    const uid = userId || (await __getAuthedUserId(supabase));
    console.log("[dbRepository.saveMatchResult] Upserting result", {
      tournamentId,
      matchId: match.id,
      hasResult: !!result,
      resultKeys: result ? Object.keys(result) : [],
      userId: uid,
    });

    const { error } = await supabase.from("match_results").upsert(
      {
        tournament_id: tournamentId,
        match_id: match.id,
        result: result || {},
        created_by: uid,
      },
      { onConflict: "match_id" },
    );

    if (error) {
      console.error("[dbRepository.saveMatchResult] Failed to upsert result", {
        tournamentId,
        matchId: match.id,
        error,
      });
      return false;
    }

    console.log("[dbRepository.saveMatchResult] ✅ Successfully saved result", {
      tournamentId,
      matchId: match.id,
      matchKey,
    });
    return true;
  },

  async saveTournamentSetup({ tournament, schedule, players } = {}) {
    console.log("[saveTournamentSetup] Starting with:", {
      code: tournament?.meta?.code,
      scheduleLength: Array.isArray(schedule) ? schedule.length : 0,
      playersCount: Array.isArray(players) ? players.length : 0,
    });

    const supabase = await getSupabaseClient();
    const code = tournament?.meta?.code;
    if (!supabase || !code) {
      console.warn("[saveTournamentSetup] Missing supabase or code");
      return { ok: false, reason: "missing-params" };
    }

    // Get tenant_id for RLS policies
    const tenantId = __getCachedTenantId();
    console.log("[saveTournamentSetup] tenantId:", tenantId);

    // Ensure tournament exists first (inkl. Team-Zulosung etc. in settings)
    const formatFromMeta =
      tournament?.meta?.settings?.format ?? tournament.format;
    const settingsWithoutFormat =
      tournament.settings && typeof tournament.settings === "object"
        ? Object.fromEntries(
            Object.entries(tournament.settings).filter(([k]) => k !== "format"),
          )
        : undefined;
    const kind = tournament?.meta?.kind || "schleifchen";
    const ensured = await this.ensureTournamentByCode({
      code,
      kind,
      name: tournament.meta?.name,
      startDate: tournament.meta?.startDate,
      startTime: tournament.meta?.startTime ?? "",
      format: formatFromMeta,
      rounds: tournament.rounds,
      minutesPerRound: tournament.minutesPerRound,
      points: tournament.points,
      settings: settingsWithoutFormat,
    });

    console.log(
      "[saveTournamentSetup] ensureTournamentByCode result:",
      ensured,
    );

    if (!ensured) {
      console.error("[saveTournamentSetup] Failed to ensure tournament:", code);
      return { ok: false, reason: "ensure-tournament-failed" };
    }

    const sch = Array.isArray(schedule) ? schedule : [];
    if (sch.length === 0) {
      console.warn(
        "[saveTournamentSetup] Empty schedule — existing rounds/matches kept",
      );
      return { ok: true, tournamentId: code, skippedSchedule: true };
    }

    // CRITICAL: Load existing matches and rounds BEFORE clearing, to preserve match_results links
    // This ensures match_results remain linked to matches after schedule update
    console.log(
      "[saveTournamentSetup] Loading existing rounds and matches to preserve match_results links",
    );

    // Load existing rounds: round_no -> round_id
    const { data: allExistingRounds } = await supabase
      .from("tournament_rounds")
      .select("id, round_no")
      .eq("tournament_id", code);

    const roundNoToOldId = new Map();
    if (Array.isArray(allExistingRounds)) {
      allExistingRounds.forEach((r) => {
        roundNoToOldId.set(r.round_no, r.id);
      });
      console.log(
        "[saveTournamentSetup] Loaded",
        allExistingRounds.length,
        "existing rounds",
      );
    }

    // Load existing matches: (round_id, match_no) -> match_id
    const { data: allExistingMatches } = await supabase
      .from("tournament_matches")
      .select("id, round_id, match_no")
      .eq("tournament_id", code);

    // Build map: (round_no, match_no) -> match_id (using old round_ids)
    const existingMatchMap = new Map();
    if (Array.isArray(allExistingMatches)) {
      allExistingMatches.forEach((m) => {
        // Find round_no for this round_id
        for (const [roundNo, roundId] of roundNoToOldId.entries()) {
          if (roundId === m.round_id) {
            const key = `${roundNo}|${m.match_no}`;
            existingMatchMap.set(key, m.id);
            break;
          }
        }
      });
      console.log(
        "[saveTournamentSetup] Loaded",
        allExistingMatches.length,
        "existing matches",
      );
    }

    // CRITICAL: Do NOT clear match_results - they are independent of schedule changes
    // Only clear matches/rounds/players, but preserve match_results
    // Clear existing sub-data before inserting new (excluding match_results)
    console.log(
      "[saveTournamentSetup] Clearing existing sub-data for:",
      code,
      "(preserving match_results)",
    );
    await __clearTournamentSubData(supabase, code);

    // Save Rounds & Matches
    console.log("[saveTournamentSetup] Saving", sch.length, "rounds");
    for (let i = 0; i < sch.length; i++) {
      const roundNo = i + 1;
      const roundPayload = { tournament_id: code, round_no: roundNo };
      if (tenantId) roundPayload.tenant_id = tenantId;

      const { data: rRow, error: rErr } = await supabase
        .from("tournament_rounds")
        .insert(roundPayload)
        .select("id")
        .single();
      if (rErr) {
        console.error(
          "[saveTournamentSetup] Error inserting round",
          roundNo,
          ":",
          rErr,
          "Payload:",
          roundPayload,
        );
        return { ok: false, reason: "db-error-rounds", error: rErr };
      }
      console.log(
        "[saveTournamentSetup] Round",
        roundNo,
        "inserted, id:",
        rRow?.id,
      );

      if (rRow?.id) {
        const matches = Array.isArray(sch[i]) ? sch[i] : [];
        console.log(
          `[saveTournamentSetup] Round ${roundNo}: ${matches.length} matches in array`,
        );
        // Filter out "bye" and "sitout" matches, only save actual matches with A/B sides
        // CRITICAL: Also accept matches without explicit type if they have A/B arrays
        const validMatches = matches.filter((m) => {
          if (!m || typeof m !== "object") return false;
          // Accept if it has A and B arrays (even if type is missing)
          const hasA = Array.isArray(m.A) && m.A.length > 0;
          const hasB = Array.isArray(m.B) && m.B.length > 0;
          const hasType = m.type === "singles" || m.type === "doubles";
          // Accept if: (has type AND has sides) OR (has both sides even without type)
          return (hasType && (hasA || hasB)) || (hasA && hasB);
        });
        console.log(
          `[saveTournamentSetup] Round ${roundNo}: ${validMatches.length} valid matches after filter`,
        );

        // CRITICAL: Load existing round_id to match_id mapping from BEFORE clearing
        // We need to find the old round_id for this round_no
        const { data: oldRounds } = await supabase
          .from("tournament_rounds")
          .select("id, round_no")
          .eq("tournament_id", code)
          .eq("round_no", roundNo)
          .limit(1);

        // Find old round_id (if any) - but since we just cleared, this won't work
        // Instead, we need to load the old rounds BEFORE clearing
        // Actually, we can't do this because we need the old round_id, but we just deleted it
        // So we need to load the old rounds BEFORE clearing

        const mRows = validMatches.map((m, idx) => {
          const matchNo = idx + 1;
          const matchPayload = {
            tournament_id: code,
            round_id: rRow.id,
            match_no: matchNo,
            court_no: m.court ? String(m.court) : null,
            side_1: Array.isArray(m.A) ? m.A : [],
            side_2: Array.isArray(m.B) ? m.B : [],
          };
          if (tenantId) matchPayload.tenant_id = tenantId;

          return matchPayload;
        });

        if (mRows.length) {
          console.log(
            "[saveTournamentSetup] Inserting",
            mRows.length,
            "matches for round",
            roundNo,
          );
          const { data: insertedMatches, error: mErr } = await supabase
            .from("tournament_matches")
            .insert(mRows)
            .select("id, match_no");

          if (mErr) {
            console.error(
              "[saveTournamentSetup] Error inserting matches for round",
              roundNo,
              ":",
              mErr,
            );
            return { ok: false, reason: "db-error-matches", error: mErr };
          }

          // CRITICAL: Re-link match_results from old match_ids to new match_ids
          if (Array.isArray(insertedMatches) && insertedMatches.length > 0) {
            for (const newMatch of insertedMatches) {
              const key = `${roundNo}|${newMatch.match_no}`;
              const oldMatchId = existingMatchMap.get(key);

              if (oldMatchId && oldMatchId !== newMatch.id) {
                // Update match_results to point to new match_id
                console.log("[saveTournamentSetup] Re-linking match_results", {
                  roundNo,
                  matchNo: newMatch.match_no,
                  oldMatchId,
                  newMatchId: newMatch.id,
                });

                const { error: updateErr } = await supabase
                  .from("match_results")
                  .update({ match_id: newMatch.id })
                  .eq("match_id", oldMatchId)
                  .eq("tournament_id", code);

                if (updateErr) {
                  console.warn(
                    "[saveTournamentSetup] Failed to re-link match_results",
                    {
                      oldMatchId,
                      newMatchId: newMatch.id,
                      error: updateErr,
                    },
                  );
                } else {
                  console.log(
                    "[saveTournamentSetup] Successfully re-linked match_results",
                    {
                      oldMatchId,
                      newMatchId: newMatch.id,
                    },
                  );
                }
              }
            }

            console.log(
              "[saveTournamentSetup] Matches for round",
              roundNo,
              "inserted successfully",
              {
                insertedCount: insertedMatches.length,
                matchIds: insertedMatches.map((m) => m.id),
              },
            );
          }
        } else {
          console.log(
            "[saveTournamentSetup] No valid matches to insert for round",
            roundNo,
          );
        }
      }
    }

    // Save Players
    // CRITICAL: Handle both array format and object format { count, names }
    let playersArray = [];
    if (Array.isArray(players)) {
      playersArray = players;
    } else if (players && typeof players === "object" && players.names) {
      // Convert { count, names: { "1": "Name1", "2": "Name2" } } to array
      const names = players.names || {};
      const count = Number(players.count || 0);
      playersArray = [];
      for (let i = 1; i <= count; i++) {
        const seed = String(i);
        const name = String(names[seed] || "").trim();
        if (name) {
          playersArray.push({
            name,
            seed: i,
            pid: players.pids?.[seed] || null,
          });
        }
      }
    }

    // Use upsert to preserve PIDs when updating existing players
    const pRows = playersArray
      .map((p, i) => {
        const name = String(p?.name || "").trim();
        const pid = String(p?.pid || "").trim();
        const playerPayload = {
          tournament_id: code,
          name,
          seed: i + 1,
        };
        // Include pid if available (for stable player IDs)
        if (pid) {
          playerPayload.pid = pid;
        }
        if (tenantId) playerPayload.tenant_id = tenantId;
        return playerPayload;
      })
      .filter((r) => r.name);

    if (pRows.length) {
      console.log(
        "[saveTournamentSetup] Upserting",
        pRows.length,
        "players with PIDs",
      );

      // Strategy: Delete existing players and insert new ones
      // This ensures we can update PIDs when names change
      // But we preserve PIDs for matching names (handled in SetupPage before calling this)
      const { error: delErr } = await supabase
        .from("tournament_players")
        .delete()
        .eq("tournament_id", code);

      if (delErr) {
        console.warn(
          "[saveTournamentSetup] Could not delete existing players:",
          delErr,
        );
        // Continue anyway - try to insert (might fail if unique constraint exists)
      }

      const { error: pErr } = await supabase
        .from("tournament_players")
        .insert(pRows);

      if (pErr) {
        console.error("[saveTournamentSetup] Error inserting players:", pErr);
        // If error is due to missing pid column, try without pid
        if (pErr.message && pErr.message.includes("pid")) {
          console.warn(
            "[saveTournamentSetup] pid column might not exist, trying without pid",
          );
          const pRowsWithoutPid = pRows.map(({ pid, ...rest }) => rest);
          const { error: pErr2 } = await supabase
            .from("tournament_players")
            .insert(pRowsWithoutPid);

          if (pErr2) {
            return { ok: false, reason: "db-error-players", error: pErr2 };
          }
        } else {
          return { ok: false, reason: "db-error-players", error: pErr };
        }
      }
      console.log("[saveTournamentSetup] Players saved successfully with PIDs");
    } else {
      console.warn("[saveTournamentSetup] No players to save");
    }

    console.log(
      "[saveTournamentSetup] ✅ Successfully saved tournament:",
      code,
    );
    return { ok: true, tournamentId: code };
  },

  /**
   * Saves ONLY a single round to the database without touching existing rounds/matches
   * Used for auto-generating next round after entry - does NOT delete existing data
   * @param {string} tournamentCode - Tournament code
   * @param {number} roundNo - Round number (1-based)
   * @param {Array} matches - Array of matches for this round
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  async saveSingleRound({ tournamentCode, roundNo, matches } = {}) {
    const supabase = await getSupabaseClient();
    const code = String(tournamentCode || "").trim();
    if (!supabase || !code || !roundNo || roundNo <= 0) {
      console.warn("[saveSingleRound] Missing required parameters");
      return { ok: false, reason: "missing-params" };
    }

    const tenantId = __getCachedTenantId();
    console.log(
      "[saveSingleRound] Saving round",
      roundNo,
      "for tournament",
      code,
    );

    try {
      // Check if round already exists
      const { data: existingRound } = await supabase
        .from("tournament_rounds")
        .select("id")
        .eq("tournament_id", code)
        .eq("round_no", roundNo)
        .single();

      let roundId;
      if (existingRound?.id) {
        // Round exists - use existing ID
        roundId = existingRound.id;
        console.log(
          "[saveSingleRound] Round",
          roundNo,
          "already exists, using existing ID:",
          roundId,
        );
      } else {
        // Create new round
        const roundPayload = { tournament_id: code, round_no: roundNo };
        if (tenantId) roundPayload.tenant_id = tenantId;

        const { data: rRow, error: rErr } = await supabase
          .from("tournament_rounds")
          .insert(roundPayload)
          .select("id")
          .single();

        if (rErr) {
          console.error(
            "[saveSingleRound] Error creating round",
            roundNo,
            ":",
            rErr,
          );
          return { ok: false, reason: "db-error-round", error: rErr };
        }
        roundId = rRow?.id;
        console.log(
          "[saveSingleRound] Created round",
          roundNo,
          "with ID:",
          roundId,
        );
      }

      if (!roundId) {
        return { ok: false, reason: "no-round-id" };
      }

      // Delete existing matches for this round (if any) before inserting new ones
      const { error: delErr } = await supabase
        .from("tournament_matches")
        .delete()
        .eq("tournament_id", code)
        .eq("round_id", roundId);

      if (delErr) {
        console.warn(
          "[saveSingleRound] Could not delete existing matches for round",
          roundNo,
          ":",
          delErr,
        );
      }

      // Filter and prepare matches
      const validMatches = (Array.isArray(matches) ? matches : []).filter(
        (m) => {
          if (!m || typeof m !== "object") return false;
          const hasA = Array.isArray(m.A) && m.A.length > 0;
          const hasB = Array.isArray(m.B) && m.B.length > 0;
          const hasType = m.type === "singles" || m.type === "doubles";
          return (hasType && (hasA || hasB)) || (hasA && hasB);
        },
      );

      if (validMatches.length === 0) {
        console.log(
          "[saveSingleRound] No valid matches to save for round",
          roundNo,
        );
        return { ok: true };
      }

      // Insert matches
      const mRows = validMatches.map((m, idx) => {
        const matchPayload = {
          tournament_id: code,
          round_id: roundId,
          match_no: idx + 1,
          court_no: m.court ? String(m.court) : null,
          side_1: Array.isArray(m.A) ? m.A : [],
          side_2: Array.isArray(m.B) ? m.B : [],
        };
        if (tenantId) matchPayload.tenant_id = tenantId;
        return matchPayload;
      });

      const { error: mErr } = await supabase
        .from("tournament_matches")
        .insert(mRows);

      if (mErr) {
        console.error(
          "[saveSingleRound] Error inserting matches for round",
          roundNo,
          ":",
          mErr,
        );
        return { ok: false, reason: "db-error-matches", error: mErr };
      }

      console.log(
        "[saveSingleRound] ✅ Successfully saved round",
        roundNo,
        "with",
        validMatches.length,
        "matches",
      );
      return { ok: true };
    } catch (e) {
      console.error("[saveSingleRound] Unexpected error:", e);
      return { ok: false, reason: "unexpected-error", error: String(e) };
    }
  },

  async updateTournamentStatus(tournamentId, roundNo, phase) {
    const supabase = await getSupabaseClient();
    if (!supabase || !tournamentId) return false;

    const roundNum = Number(roundNo || 0);
    if (roundNum <= 0) {
      console.warn("[updateTournamentStatus] Invalid roundNo:", roundNo);
      return false;
    }

    console.log("[updateTournamentStatus] Updating tournament status", {
      tournamentId,
      roundNo: roundNum,
      phase,
    });

    // CRITICAL: Also update completed_round to roundNo - 1 (the round that was just completed)
    const completedRound = roundNum - 1;

    const { error } = await supabase
      .from("tournaments")
      .update({
        current_round: roundNum,
        completed_round: completedRound,
        phase,
        updated_at: new Date().toISOString(),
      })
      .eq("code", tournamentId);

    if (error) {
      console.error("[updateTournamentStatus] Failed to update:", error);
      return false;
    }

    console.log("[updateTournamentStatus] ✅ Successfully updated", {
      tournamentId,
      current_round: roundNum,
      completed_round: completedRound,
      phase,
    });

    return true;
  },
};

// ------------------------------------------------------------
// Public Data Loader
// ------------------------------------------------------------
export async function getFullSchedule(tournamentId) {
  const supabase = await getSupabaseClient();
  if (!supabase || !tournamentId) return [];

  const { data: rounds, error } = await supabase
    .from("tournament_rounds")
    .select(
      `
      round_no,
      tournament_matches (
        id, match_no, court_no, side_1, side_2
      )
    `,
    )
    .eq("tournament_id", tournamentId)
    .order("round_no", { ascending: true });

  if (error) return [];

  return rounds.map((r) => {
    const roundNo = Number(r.round_no || 0);
    return {
      round: roundNo,
      matches: (r.tournament_matches || [])
        .sort((a, b) => (a.match_no || 0) - (b.match_no || 0))
        .map((m) => {
          const matchNo = Number(m.match_no || 0);
          // WICHTIG: id muss mit dem Key übereinstimmen, der beim Laden der Ergebnisse verwendet wird
          // Der Key wird in getMatchResults als r{roundNo}m{matchNo} generiert
          const matchKey =
            roundNo > 0 && matchNo > 0 ? `r${roundNo}m${matchNo}` : null;
          return {
            id: matchKey, // Muss mit Ergebnis-Keys übereinstimmen (r1m1, r1m2, etc.)
            match_no: matchNo, // Für persistenceservice.js
            court: m.court_no || "",
            A: m.side_1 || [],
            B: m.side_2 || [],
            type:
              m.side_1?.length === 1 && m.side_2?.length === 1
                ? "singles"
                : "doubles",
          };
        }),
    };
  });
}
